import type {
  controlLineVisibleAttestationOptions,
  drugScreenTestTypeOrder,
  temperatureInRangeAttestationOptions
} from '@/constants/drugScreen';

import type { AffectUser } from './affectUser';
import type { Appointment } from './appointments';
import type { Person } from './person';

export interface DrugScreen {
  // Substances
  amphetamines?: string | null;
  appointment: Appointment | null;
  assignment_type: string;
  bac?: string | null;
  benzos?: string | null;
  buprenorphine?: string | null;
  cocaine?: string | null;
  conducted_by: AffectUser | null;
  control_line_visible_attestation: keyof typeof controlLineVisibleAttestationOptions;
  drug_screen_result_reason: DrugScreenResultReason[];
  drug_screen_test_type: DefaultDrugScreenDataTestType | null;
  id: string;
  is_compliant: boolean;
  is_mutable: boolean;
  kind: string;
  lot_number: string | null;
  mdma?: string | null;
  member_in_attendance: string;
  methadone?: string | null;
  methamphetamines?: string | null;
  nortriptyline?: string | null;
  notes?: string | null;
  occurred_at: string;
  opiates?: string | null;
  oxazepam?: string | null;
  oxycontin?: string | null;
  payment_line_item: DrugScreenPaymentLineItem[];
  payout: DrugScreenPayout;
  pcp?: string | null;
  person: Pick<Person, 'id' | 'first_name' | 'last_name' | 'time_zone'>;
  result: DrugScreenResult;
  secobarbital?: string | null;
  temperature_in_range_attestation: keyof typeof temperatureInRangeAttestationOptions | null;
  test_expiration_on: string | null;
  thc?: string | null;
}

export enum DrugScreenResult {
  ABANDONED = 'abandoned',
  FORMAL_NEGATIVE = 'formal_negative',
  FORMAL_POSITIVE = 'formal_positive',
  IN_PROGRESS = 'in_progress',
  INVALID = 'invalid',
  MISSED = 'missed',
  NEGATIVE = 'negative',
  POSITIVE = 'positive'
}

export interface DrugScreenTestType {
  active: boolean;
  assumed_detection_window: number;
  brand: string;
  created_at: string;
  id: string;
  kind: string;
  manufacturer: string;
  model_number: string;
  updated_at: string;
}

export interface DefaultDrugScreenData {
  drug_screen_result_reasons: DrugScreenResultReason[];
  drug_screen_test_types: DefaultDrugScreenDataTestType[];
  HEALTHIE_URL: string;
}

export interface DrugScreenPaymentLineItem {
  id: string;
  money: string;
  status: string;
  type: string;
}

export interface DrugScreenPayout {
  attendance: number;
  base: number;
  bonus: number;
  is_compliant: boolean;
  payout_in_cents: number;
  test_administration: number;
}

export interface DrugScreenResultReason {
  active: boolean;
  id: string;
  name: string;
}

export interface DefaultDrugScreenDataTestType {
  active: boolean;
  id: string;
  kind: keyof typeof drugScreenTestTypeOrder;
  name: string;
  test_strips: TestStrip[];
}

export interface TestStrip {
  drug_screen_test_type: string;
  id: string;
  internal_drug_field: string;
  name: string;
}

export interface PersonDrugScreensCounts {
  last_member_drug_screen?: DrugScreen | null;
  positive: number;
  negative: number;
  total: number;
  is_compliant: number;
  is_non_compliant: number;
}

export type CompliantResult = 'Positive' | 'Negative';
